import React from 'react';
import {styled} from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import Report from '@mui/icons-material/Report';
import {FuelBadgeVariant, getBoldColors, getDefaultColors} from '../helpers/getColor';
import {Box, BoxProps} from '../Box';
import clsx from 'clsx';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface BadgeProps extends Pick<BoxProps, 'sx' | 'children' | 'className'> {
    /**
     * Fuel specific color variant that maps
     * to semantic colors used in Alert/Badge
     */
    variant?: FuelBadgeVariant;
    /**
     * The component maps the variant prop to a range of different icons. For
     * instance, success to `<SuccessOutlined>`. If you wish to change this mapping,
     * you can provide your own. Alternatively, you can use the startIcon / endIcon prop to override
     * the icon displayed.
     */
    iconMapping?: Record<'default', Record<FuelBadgeVariant, React.ReactNode>>;
    /**
     * Customize icon at the start of the Badge.
     * Default icon for variant is used when custom is
     * not provided.
     */
    startIcon?: React.ReactNode;
    /**
     * Type of Badge
     */
    type?: 'default' | 'bold';
}

const badgeIcons: Record<'default', Record<FuelBadgeVariant, React.ReactNode>> = {
    default: {
        info: <InfoOutlined titleAccess={'Info:'}/>,
        success: <CheckCircle titleAccess={'Success:'}/>,
        warning: <Warning titleAccess={'Warning:'}/>,
        error: <Report titleAccess={'Error:'}/>
    }
};

const StyledBadge = styled(Box, {
    ...excludeComponentProps('variant', 'type')
})<BadgeProps>(({
    theme,
    variant,
    type
}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: theme.tokens.FuelFontSizeXs,
    borderRadius: theme.tokens.FuelBorderRadiusLg,
    fontWeight: theme.tokens.FuelFontWeightSemibold,
    justifyContent: 'center',
    lineHeight: '1rem',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '4px',
    columnGap: '.125rem',
    '& .FuelBadge-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    '&& .MuiSvgIcon-root': {
        lineHeight: '1rem',
        fontSize: '1rem'
    },
    ...type === 'default' && getDefaultColors(theme, variant),
    ...type === 'bold' && getBoldColors(theme, variant)
}));

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(({
    children,
    variant,
    startIcon,
    iconMapping,
    className,
    type = 'default',
    ...props
}, ref) => {
    const badgeIconMap = {
        ...badgeIcons,
        ...iconMapping
    };

    const resolvedBadgeIcon = variant && badgeIconMap.default[variant];

    return (
        <StyledBadge
            className={clsx('FuelBadge', className)}
            ref={ref}
            type={type}
            variant={variant}
            {...props}
        >
            {startIcon === true && resolvedBadgeIcon || startIcon}
            <span className={'FuelBadge-label'}>{children}</span>
        </StyledBadge>
    );
});

Badge.displayName = 'Badge';
